import { fetchComCatBrands, fetchComCatVariantsForBrand } from '@/api/comCatBrand';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { readSmokerProfileAnswers, writeSmokerProfileAnswers } from '@/lib/smokerProfileStorage';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const RACES = ['Malay', 'Chinese', 'Indian', 'Others'] as const;

const MY_STATES = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Kuala Lumpur',
  'Melaka',
  'Negeri Sembilan',
  'Pahang',
  'Perak',
  'Perlis',
  'Pulau Pinang',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
] as const;

const selectClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-70';

const titleClass =
  'm-0 mb-3 text-left text-[16px] font-medium text-white/95 sm:text-[17px]';

export default function SmokerProfile() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [race, setRace] = useState(() => readSmokerProfileAnswers()?.race ?? '');
  const [state, setState] = useState(() => readSmokerProfileAnswers()?.state ?? '');
  const [brand, setBrand] = useState(() => readSmokerProfileAnswers()?.brand ?? '');
  const [variant, setVariant] = useState(() => readSmokerProfileAnswers()?.variant ?? '');
  const [nextWarning, setNextWarning] = useState<string | null>(null);
  const [brands, setBrands] = useState<string[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [variants, setVariants] = useState<string[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  useEffect(() => {
    if (surveyLanguage == null || surveyScreening == null || !pdpaConsent) {
      return;
    }
    if (!mobileLocalDigits.replace(/\D/g, '') || !customerUuid) {
      return;
    }

    let cancelled = false;
    setBrandsLoading(true);
    void (async () => {
      try {
        const list = await fetchComCatBrands();
        if (!cancelled) {
          setBrands(list);
        }
      } finally {
        if (!cancelled) {
          setBrandsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [surveyLanguage, surveyScreening, pdpaConsent, mobileLocalDigits, customerUuid]);

  useEffect(() => {
    if (!brand) {
      setVariants([]);
      setVariantsLoading(false);
      return;
    }

    let cancelled = false;
    setVariantsLoading(true);
    void (async () => {
      try {
        const list = await fetchComCatVariantsForBrand(brand);
        if (!cancelled) {
          setVariants(list);
        }
      } finally {
        if (!cancelled) {
          setVariantsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [brand]);

  useEffect(() => {
    writeSmokerProfileAnswers({
      race,
      state,
      brand,
      variant,
    });
  }, [race, state, brand, variant]);

  useEffect(() => {
    setNextWarning(null);
  }, [race, state, brand, variant]);

  const handleNext = () => {
    if (brandsLoading) {
      setNextWarning(t.smokerProfile.brandsLoadingWarning);
      return;
    }
    if (brand && variantsLoading) {
      setNextWarning(t.smokerProfile.variantsLoadingWarning);
      return;
    }
    const r = race.trim();
    const s = state.trim();
    const b = brand.trim();
    const v = variant.trim();
    if (!r || !s || !b || !v) {
      setNextWarning(t.smokerProfile.incompleteWarning);
      return;
    }
    writeSmokerProfileAnswers({ race: r, state: s, brand: b, variant: v });
    setNextWarning(null);
    navigate('/7_1');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <p className={titleClass}>{t.smokerProfile.raceLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <select
                id="smoker-race"
                name="race"
                value={race}
                onChange={(e) => setRace(e.target.value)}
                className={selectClassName}
              >
                <option value="">{t.smokerProfile.selectRace}</option>
                {RACES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <p className={titleClass}>{t.smokerProfile.stateLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <select
                id="smoker-state"
                name="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={selectClassName}
              >
                <option value="">{t.smokerProfile.selectState}</option>
                {MY_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <p className={titleClass}>{t.smokerProfile.brandLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <div className="flex flex-col gap-4">
                <select
                  id="smoker-brand"
                  name="brand"
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setVariant('');
                  }}
                  className={selectClassName}
                  aria-label="Select a brand"
                  disabled={brandsLoading}
                >
                  <option value="">
                    {brandsLoading ? t.smokerProfile.loadingBrands : t.smokerProfile.selectBrand}
                  </option>
                  {brands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
                <select
                  id="smoker-variant"
                  name="variant"
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className={selectClassName}
                  aria-label="Select a variant"
                  disabled={!brand || variantsLoading}
                >
                  <option value="">
                    {!brand
                      ? t.smokerProfile.selectBrandFirst
                      : variantsLoading
                        ? t.smokerProfile.loadingVariants
                        : t.smokerProfile.selectVariant}
                  </option>
                  {variants.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {nextWarning != null ? <SurveyValidationAlert>{nextWarning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={handleNext}
              >
                {t.smokerProfile.next}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
